import React, { useState, useEffect } from 'react'
import { Button } from 'flowbite-react';
import { useEventApi } from "../../store";
import TicketInfo from './TicketInfo';
import Loader from '../shared/Loader';
import AlertComponent from '../shared/AlertComponent';
import BackButton from '../shared/BackButton';

const TicketSearch = () => {
    const { getTickets, sendTicketsToBuyerViaEmail } = useEventApi();

    const [currentSubView, setCurrentSubView] = useState<string>('search');
    const [message, setMessage] = useState<string>('');
    const [eventId, setEventId] = useState<string>('');
    const [ticketId, setTicketId] = useState<string>('');
    const [username, setUsername] = useState<string>('');
    const [eventName, setEventName] = useState<string>('');
    const [eventLink, setEventLink] = useState<string>('');
    const [guest, setGuest] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [tickets, setTickets] = useState<any[]>([]);
    // lifted from TicketInfo so the email button always sends the selected ticket
    const [selectedTicketIndex, setSelectedTicketIndex] = useState<number>(0);

    useEffect(() => {
        document.title = 'FestiShare Support - Tickets';
    }, []);

    const getTicketsInfo = async (ticketId: string, eventId: string, username: string) => {
        setCurrentSubView('loader');
        try {
            const result: any = await getTickets(ticketId, eventId, username);
            if (result?.res === 'success') {
                setTickets(result.data.tickets);
                setEventId(result.data.event_id);
                setEventName(result.data.event_name);
                setEventLink(result.data.event_link);
                setGuest(result.data.guest);
                setEmail(result.data.email);
                setSelectedTicketIndex(0);
                setCurrentSubView('default');
            } else {
                setMessage(result?.message || 'Ticket not found.');
                setCurrentSubView('error');
            }
        } catch {
            setMessage('An unexpected error occurred.');
            setCurrentSubView('error');
        }
    };

    const sendTicketsViaEmail = async () => {
        const selectedTicket = tickets[selectedTicketIndex];
        if (!selectedTicket) return;
        try {
            const result: any = await sendTicketsToBuyerViaEmail(selectedTicket.id);
            if (result?.res === 'success') {
                setMessage('Email sent successfully.');
                setCurrentSubView('success');
            } else {
                setMessage(result?.message || 'Failed to send email.');
                setCurrentSubView('error');
            }
        } catch {
            setMessage('An unexpected error occurred.');
            setCurrentSubView('error');
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const ticketIdValue = e.currentTarget.ticketId.value;
        const eventIdValue = e.currentTarget.eventId.value;
        const usernameValue = e.currentTarget.username.value;
        setTicketId(ticketIdValue);
        setEventId(eventIdValue);
        setUsername(usernameValue);
        getTicketsInfo(ticketIdValue, eventIdValue, usernameValue);
    };

    return (
        <div className="mt-12 mb-20">
            <div className="mx-auto max-w-3xl px-6 lg:px-8">
                <div className="flex justify-center">
                    <h2 className="text-base font-semibold leading-7 text-gray-900 underline">
                        Information about the Ticket
                    </h2>
                </div>

                {currentSubView === 'loader' && <Loader />}

                {currentSubView === 'search' && (
                    <form onSubmit={handleSubmit}>
                        <div className="mb-2 block mt-6">
                            <label htmlFor="ticketId" className="block text-sm font-medium leading-6 text-gray-900">
                                Ticket ID
                            </label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="ticketId"
                                    id="ticketId"
                                    defaultValue={ticketId}
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                />
                            </div>
                        </div>

                        <div className="mt-3 w-full flex justify-between items-center">
                            <hr className="w-2/5" />
                            <span className="uppercase">OR</span>
                            <hr className="w-2/5" />
                        </div>

                        <div className="mb-2 block mt-6">
                            <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900">
                                Username / email
                            </label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="username"
                                    id="username"
                                    defaultValue={username}
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                />
                            </div>
                        </div>

                        <div className="mt-3 w-full flex justify-between items-center">
                            <hr className="w-2/5" />
                            <span className="uppercase">AND</span>
                            <hr className="w-2/5" />
                        </div>

                        <div className="mb-2 block mt-6">
                            <label htmlFor="eventId" className="block text-sm font-medium leading-6 text-gray-900">
                                Event Id
                            </label>
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="eventId"
                                    id="eventId"
                                    defaultValue={eventId}
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                />
                            </div>
                        </div>

                        <div className="w-full flex justify-center mt-6">
                            <Button type='submit'>Continue</Button>
                        </div>
                    </form>
                )}

                {tickets.length > 0 && currentSubView === 'default' && (
                    <div>
                        <div className="flex flex-col items-center mt-4 mb-2 gap-1">
                            <p className="text-base text-green-600 leading-7">Guest: {guest}</p>
                            <p className="text-base text-green-600 leading-7">Email: {email}</p>
                            <p className="text-base text-green-600 leading-7">Event Name: {eventName}</p>
                            <p className="text-base text-green-600 leading-7">Event Id: {eventId.slice(-9)}</p>
                            <a target="_blank" href={eventLink} rel="noreferrer" className="text-base text-blue-600 leading-7">
                                See Event
                            </a>
                            <div className="w-full flex justify-center mt-3 mb-3">
                                <Button type='button' onClick={sendTicketsViaEmail}>
                                    Email ticket #{selectedTicketIndex + 1} to guest
                                </Button>
                            </div>
                        </div>

                        <TicketInfo
                            tickets={tickets}
                            selectedTicketIndex={selectedTicketIndex}
                            onTicketChange={setSelectedTicketIndex}
                        />
                        <BackButton callBack={() => setCurrentSubView('search')} />
                    </div>
                )}

                {currentSubView === 'success' && (
                    <AlertComponent
                        callBack={() => setCurrentSubView('search')}
                        type='success'
                        subject='Success'
                        message={message}
                    />
                )}

                {currentSubView === 'error' && (
                    <AlertComponent
                        callBack={() => setCurrentSubView('search')}
                        type='error'
                        subject='Failed'
                        message={message}
                    />
                )}
            </div>
        </div>
    );
};

export default TicketSearch;
